import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ReserveHeader } from "@/components/ReserveHeader";
import { getVenueKpis, type VenueKpis } from "@/lib/venue-kpis.functions";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/venue-kpis")({
  ssr: false,
  beforeLoad: async () => {
    const { data, error } = await supabase.auth.getUser();
    if (error || !data.user) throw redirect({ to: "/auth" });

    const { data: roleRow } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", data.user.id)
      .eq("role", "venue")
      .maybeSingle();

    if (!roleRow) throw redirect({ to: "/dashboard" });
  },
  head: () => ({
    meta: [
      { title: "Venue KPIs — The Hospitality Reserve" },
      { name: "description", content: "Operational metrics for your venue: bookings, spend, fill rate, and bartender performance over time." },
      { name: "robots", content: "noindex,nofollow" },
    ],
    links: [{ rel: "canonical", href: "https://thehospitalityreserve.com/venue-kpis" }],
  }),
  component: VenueKpisPage,
});

const fmtGbp = (pence: number) => `\u00a3${(pence / 100).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const monthLabel = (ym: string) => {
  const [y, m] = ym.split("-");
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString("en-GB", { month: "short", year: "2-digit" });
};

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="border border-border bg-card p-5">
      <p className="text-xs tracking-wide-luxury text-muted-foreground uppercase">{label}</p>
      <p className="mt-3 font-display text-2xl">{value}</p>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

const DUMMY_PREVIOUS_BARTENDERS = [
  { id: "d1", name: "Oliver Hawthorne", shifts: 14, last_worked: "2026-05-22", rating: 4.9, spend_pence: 487550, specialty: "Classic & vintage cocktails" },
  { id: "d2", name: "Amelia Sinclair", shifts: 9, last_worked: "2026-05-08", rating: 4.8, spend_pence: 312000, specialty: "Champagne service" },
  { id: "d3", name: "Marcus Devereux", shifts: 6, last_worked: "2026-04-30", rating: 4.6, spend_pence: 198750, specialty: "Whisky flights" },
  { id: "d4", name: "Sienna Whitcombe", shifts: 4, last_worked: "2026-04-12", rating: 4.7, spend_pence: 142400, specialty: "Aperitivo & spritz bar" },
  { id: "d5", name: "Rafael Costa", shifts: 3, last_worked: "2026-03-29", rating: 4.5, spend_pence: 105600, specialty: "Tropical & tiki" },
];

type Tab = "overview" | "previous";

function VenueKpisPage() {
  const fetchKpis = useServerFn(getVenueKpis);
  const [kpis, setKpis] = useState<VenueKpis | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("overview");

  useEffect(() => {
    fetchKpis().then(setKpis).catch((e) => setErr(e instanceof Error ? e.message : "Could not load KPIs."));
  }, [fetchKpis]);

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role="venue" />
      <main className="mx-auto max-w-7xl px-6 py-10">
        <div className="flex items-end justify-between gap-6">
          <div>
            <p className="tracking-wide-luxury text-accent">Operations</p>
            <h1 className="mt-3 font-display text-3xl">Performance & KPIs</h1>
            <span className="gold-divider mt-4" />
          </div>
          <Link to="/venue" className="border border-border px-4 py-2 text-xs tracking-wide-luxury text-muted-foreground hover:text-foreground hover:border-foreground">← Return to Lobby</Link>
        </div>

        {err && <p className="mt-8 text-sm text-destructive">{err}</p>}
        {!kpis && !err && <p className="mt-8 text-sm text-muted-foreground">Loading KPIs…</p>}

        {kpis && (
          <>
            {/* Headline cards: total shifts + total spend (always visible) */}
            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              <Stat label="Total Shifts Booked" value={String(kpis.totals.paid + kpis.totals.filled + kpis.totals.completed)} hint={`${kpis.totals.posted} posted in total`} />
              <Stat label="Total Spend" value={fmtGbp(kpis.spend.total_pence)} hint={`${kpis.spend.total_hours} hrs staffed · avg ${kpis.spend.avg_hourly_rate != null ? `\u00a3${kpis.spend.avg_hourly_rate.toFixed(2)}/hr` : "—"}`} />
            </div>

            {/* Tabs */}
            <div className="mt-10 flex gap-2 border-b border-border">
              {([
                { id: "overview", label: "Overview" },
                { id: "previous", label: "Previous Bartenders" },
              ] as { id: Tab; label: string }[]).map((t) => (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`px-5 py-3 text-xs tracking-wide-luxury uppercase border-b-2 -mb-px transition-colors ${tab === t.id ? "border-accent text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {tab === "previous" && (
              <section className="mt-8">
                <p className="text-xs text-muted-foreground mb-4">Sample roster shown — populates with your real engagement history as shifts complete.</p>
                <div className="border border-border bg-card">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs tracking-wide-luxury text-muted-foreground uppercase">
                        <th className="px-5 py-3">Bartender</th>
                        <th className="px-5 py-3">Specialty</th>
                        <th className="px-5 py-3">Shifts</th>
                        <th className="px-5 py-3">Last Worked</th>
                        <th className="px-5 py-3">Spend</th>
                        <th className="px-5 py-3">Rating</th>
                      </tr>
                    </thead>
                    <tbody>
                      {DUMMY_PREVIOUS_BARTENDERS.map((b) => (
                        <tr key={b.id} className="border-t border-border">
                          <td className="px-5 py-3 font-medium">{b.name}</td>
                          <td className="px-5 py-3 text-muted-foreground">{b.specialty}</td>
                          <td className="px-5 py-3">{b.shifts}</td>
                          <td className="px-5 py-3 text-muted-foreground">{new Date(b.last_worked).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</td>
                          <td className="px-5 py-3">{fmtGbp(b.spend_pence)}</td>
                          <td className="px-5 py-3 text-accent">★ {b.rating}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {tab === "overview" && (
          <div className="mt-8 space-y-12">
            {/* Spend */}
            <section>
              <h2 className="font-display text-xl">Spend</h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Stat label="Lifetime Spend" value={fmtGbp(kpis.spend.total_pence)} hint="Paid, filled & completed engagements" />
                <Stat label="Upcoming Committed" value={fmtGbp(kpis.spend.upcoming_committed_pence)} hint="Paid shifts not yet completed" />
                <Stat label="Avg / Completed Shift" value={fmtGbp(kpis.spend.avg_per_shift_pence)} />
                <Stat label="Avg Hourly Rate Posted" value={kpis.spend.avg_hourly_rate != null ? `\u00a3${kpis.spend.avg_hourly_rate.toFixed(2)}` : "—"} hint={`${kpis.spend.total_hours} total hrs staffed`} />
              </div>
            </section>

            {/* Pipeline */}
            <section>
              <h2 className="font-display text-xl">Engagement Pipeline</h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Stat label="Posted" value={String(kpis.totals.posted)} />
                <Stat label="Open" value={String(kpis.totals.open)} hint="Awaiting applicants / selection" />
                <Stat label="Awaiting Payment" value={String(kpis.totals.awaiting_payment)} />
                <Stat label="Paid / Filled" value={String(kpis.totals.paid + kpis.totals.filled)} />
                <Stat label="Completed" value={String(kpis.totals.completed)} />
                <Stat label="Cancelled" value={String(kpis.totals.cancelled)} />
                <Stat label="Disputed" value={String(kpis.totals.disputed)} />
                <Stat label="Auto-Confirmed" value={String(kpis.performance.auto_confirmed_count)} hint="Closed automatically after 48h" />
              </div>
            </section>

            {/* Performance */}
            <section>
              <h2 className="font-display text-xl">Performance</h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Stat label="Fill Rate" value={`${kpis.performance.fill_rate}%`} hint="Of posted shifts" />
                <Stat label="Cancellation Rate" value={`${kpis.performance.cancellation_rate}%`} />
                <Stat label="Avg Time to Fill" value={kpis.performance.avg_hours_to_fill != null ? `${kpis.performance.avg_hours_to_fill}h` : "—"} hint="Post → applicant accepted" />
                <Stat label="DBS Required Share" value={`${kpis.performance.dbs_share}%`} />
              </div>
            </section>

            {/* Bartender relationships */}
            <section>
              <h2 className="font-display text-xl">Bartender Relationships</h2>
              <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <Stat label="Unique Bartenders" value={String(kpis.bartenders.unique_count)} />
                <Stat label="Repeat Bartenders" value={String(kpis.bartenders.repeat_count)} hint="Worked 2+ engagements" />
                <Stat label="Favorites" value={String(kpis.bartenders.favorites_count)} />
                <Stat label="Avg Rating Given" value={kpis.bartenders.avg_rating_given != null ? `★ ${kpis.bartenders.avg_rating_given}` : "—"} hint={`Received: ${kpis.bartenders.avg_rating_received != null ? `★ ${kpis.bartenders.avg_rating_received}` : "—"}`} />
              </div>

              <div className="mt-6 border border-border bg-card">
                <div className="px-5 py-3 border-b border-border text-xs tracking-wide-luxury text-muted-foreground uppercase">Top Bartenders by Completed Shifts</div>
                {kpis.bartenders.top.length === 0 ? (
                  <div className="px-5 py-6 text-sm text-muted-foreground">No completed engagements yet.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs tracking-wide-luxury text-muted-foreground uppercase">
                        <th className="px-5 py-3">Bartender</th>
                        <th className="px-5 py-3">Shifts</th>
                        <th className="px-5 py-3">Spend</th>
                        <th className="px-5 py-3">Rating</th>
                      </tr>
                    </thead>
                    <tbody>
                      {kpis.bartenders.top.map((b) => (
                        <tr key={b.bartender_id} className="border-t border-border">
                          <td className="px-5 py-3">{b.full_name ?? "—"}</td>
                          <td className="px-5 py-3">{b.shifts}</td>
                          <td className="px-5 py-3">{fmtGbp(b.spend_pence)}</td>
                          <td className="px-5 py-3">{b.avg_rating != null ? `★ ${b.avg_rating}` : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </section>

            {/* Monthly trend */}
            <section>
              <h2 className="font-display text-xl">Last 6 Months</h2>
              <div className="mt-4 border border-border bg-card">
                {kpis.monthly.length === 0 ? (
                  <div className="px-5 py-6 text-sm text-muted-foreground">No completed engagements yet.</div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs tracking-wide-luxury text-muted-foreground uppercase">
                        <th className="px-5 py-3">Month</th>
                        <th className="px-5 py-3">Completed</th>
                        <th className="px-5 py-3">Hours</th>
                        <th className="px-5 py-3">Spend</th>
                        <th className="px-5 py-3 w-1/3">Volume</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const max = Math.max(...kpis.monthly.map((m) => m.completed), 1);
                        return kpis.monthly.map((m) => (
                          <tr key={m.month} className="border-t border-border">
                            <td className="px-5 py-3">{monthLabel(m.month)}</td>
                            <td className="px-5 py-3">{m.completed}</td>
                            <td className="px-5 py-3">{m.hours}</td>
                            <td className="px-5 py-3">{fmtGbp(m.spend_pence)}</td>
                            <td className="px-5 py-3">
                              <div className="h-2 bg-muted">
                                <div className="h-full bg-accent" style={{ width: `${(m.completed / max) * 100}%` }} />
                              </div>
                            </td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                )}
              </div>
            </section>
          </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
